/**
 * Structured Term Sheet Fields Component
 *
 * Provides structured input fields for deal term expectations:
 * - Upfront payment range
 * - Milestone payment range
 * - Royalty range
 * - Territory rights
 */

import { Lightbulb } from 'lucide-react';

// Upfront payment ranges for deals
export const UPFRONT_RANGES = [
  { value: '', label: 'Select range' },
  { value: '<5M', label: '< $5M' },
  { value: '5-10M', label: '$5M - $10M' },
  { value: '10-25M', label: '$10M - $25M' },
  { value: '25-50M', label: '$25M - $50M' },
  { value: '50-100M', label: '$50M - $100M' },
  { value: '100-200M', label: '$100M - $200M' },
  { value: '>200M', label: '> $200M' },
  { value: 'flexible', label: 'Flexible / Negotiable' },
];

// Milestone ranges for deals
export const MILESTONE_RANGES = [
  { value: '', label: 'Select range' },
  { value: '<50M', label: '< $50M' },
  { value: '50-100M', label: '$50M - $100M' },
  { value: '100-300M', label: '$100M - $300M' },
  { value: '300-500M', label: '$300M - $500M' },
  { value: '500M-1B', label: '$500M - $1B' },
  { value: '>1B', label: '> $1B' },
  { value: 'flexible', label: 'Flexible / Negotiable' },
];

// Royalty ranges for deals
export const ROYALTY_RANGES = [
  { value: '', label: 'Select range' },
  { value: 'single-digit', label: 'Single digit (1-9%)' },
  { value: 'low-double', label: 'Low double digit (10-14%)' },
  { value: 'mid-double', label: 'Mid double digit (15-19%)' },
  { value: 'high-double', label: 'High double digit (20%+)' },
  { value: 'tiered', label: 'Tiered (sales-based)' },
  { value: 'flexible', label: 'Flexible / Negotiable' },
];

// Territory options for deals
export const TERRITORY_OPTIONS = [
  { value: 'global', label: 'Global' },
  { value: 'global-ex-china', label: 'Global ex-China' },
  { value: 'global-ex-asia', label: 'Global ex-Asia' },
  { value: 'north-america', label: 'North America' },
  { value: 'europe', label: 'Europe' },
  { value: 'asia-pacific', label: 'Asia Pacific' },
  { value: 'china', label: 'Greater China' },
  { value: 'japan', label: 'Japan' },
  { value: 'row', label: 'Rest of World' },
];

/**
 * TermSheetFields Component
 *
 * @param {Object} props
 * @param {string} props.upfrontRange - Selected upfront range
 * @param {string} props.milestoneRange - Selected milestone range
 * @param {string} props.royaltyRange - Selected royalty range
 * @param {string[]} props.territoryRights - Selected territory options
 * @param {string} props.termSheetNotes - Additional notes
 * @param {function} props.onChange - Callback when any field changes
 * @param {boolean} props.showHelp - Whether to show help text
 */
export function TermSheetFields({
  upfrontRange = '',
  milestoneRange = '',
  royaltyRange = '',
  territoryRights = [],
  termSheetNotes = '',
  onChange,
  showHelp = true,
}) {
  const handleChange = (field, value) => {
    onChange?.({ field, value });
  };

  const handleTerritoryChange = (territoryValue, checked) => {
    let newTerritories;
    if (checked) {
      newTerritories = [...territoryRights, territoryValue];
    } else {
      newTerritories = territoryRights.filter(t => t !== territoryValue);
    }
    handleChange('territory_rights', newTerritories);
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">
          Term Sheet Expectations
        </h4>
      </div>

      {showHelp && (
        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              Reference comparable deals in your therapeutic area and stage.
              Realistic expectations lead to faster negotiations.
              "Flexible" signals openness to discuss.
            </p>
          </div>
        </div>
      )}

      {/* Structured Fields Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Upfront Range */}
        <div>
          <label className="label">Upfront Payment</label>
          <select
            value={upfrontRange}
            onChange={(e) => handleChange('upfront_range', e.target.value)}
            className="input"
          >
            {UPFRONT_RANGES.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Milestone Range */}
        <div>
          <label className="label">Total Milestones</label>
          <select
            value={milestoneRange}
            onChange={(e) => handleChange('milestone_range', e.target.value)}
            className="input"
          >
            {MILESTONE_RANGES.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Royalty Range */}
        <div>
          <label className="label">Royalty Rate</label>
          <select
            value={royaltyRange}
            onChange={(e) => handleChange('royalty_range', e.target.value)}
            className="input"
          >
            {ROYALTY_RANGES.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Territory Rights */}
        <div>
          <label className="label">Territory Rights Available</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {TERRITORY_OPTIONS.map(opt => (
              <label
                key={opt.value}
                className={`px-2 py-1 rounded-full text-xs cursor-pointer transition-colors ${
                  territoryRights.includes(opt.value)
                    ? 'bg-purple-100 text-purple-700 border border-purple-300'
                    : 'bg-gray-100 text-gray-600 border border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={territoryRights.includes(opt.value)}
                  onChange={(e) => handleTerritoryChange(opt.value, e.target.checked)}
                  className="sr-only"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      <div>
        <label className="label">Additional Term Notes (optional)</label>
        <textarea
          value={termSheetNotes}
          onChange={(e) => handleChange('term_sheet_expectations', e.target.value)}
          className="input min-h-[60px]"
          placeholder="e.g., Prefer upfront-heavy structure, open to co-promotion rights, etc."
        />
        <p className="text-xs text-gray-500 mt-1 italic">
          Add any specific requirements or preferences not covered above.
        </p>
      </div>
    </div>
  );
}

export default TermSheetFields;
